import { QueryKey } from '@tanstack/query-core';

import { appAuthenticator } from '../../shared-services/src/app-core/setup/app-authenticator';
import { clearCurrentUserQueryCache, CurrentUserProfile, currentUserQueryKey } from '../queries/currentUser';
import { clearUserPropertiesQueryCache, userPropertiesQueryKey } from '../queries/userProperties';
import { createMutation } from '../utils/mutations';
import { queryClient } from '../core/queryClient';
import { updateCurrentUser } from '../../shared-services/src/users/api';
import { updateUserProperty } from '../../users/src/core/api';

// Queue by query instance, not just key, so a replacement session does not inherit the old write queue.
const pendingWrites = new WeakMap<object, Promise<unknown>>();

/**
 * Serializes writes to one query instance and rejects queued work if that instance is removed/replaced.
 * Reconciles only the still-current session's cache, using the supplied success handler.
 * An already-sent backend write is not canceled by the session guard.
 */
async function mutateCurrentUser<TResult>(
  mutation: (isCurrent: () => boolean) => Promise<TResult>,
  queryKey: QueryKey,
  onSuccess: (result: TResult) => Promise<void> | void
): Promise<TResult> {
  const query = queryClient.getQueryCache().build<unknown>(queryClient, { queryKey });
  const isCurrentQuery = () => queryClient.getQueryCache().find({ exact: true, queryKey }) === query;
  const request = (pendingWrites.get(query) ?? Promise.resolve())
    // A failed earlier write must not prevent later queued writes from running.
    .catch(() => undefined)
    .then(async () => {
      if (!isCurrentQuery()) {
        throw new Error('Current-user update canceled after authentication changed');
      }
      // Stop older reads from racing the write; cancellation itself is an async session boundary.
      await queryClient.cancelQueries({ exact: true, queryKey });
      if (!isCurrentQuery()) {
        throw new Error('Current-user update canceled after authentication changed');
      }
      const result = await mutation(isCurrentQuery);
      if (isCurrentQuery()) {
        // Reads may have started during the write; cancel them before applying confirmed data or invalidating.
        await queryClient.cancelQueries({ exact: true, queryKey });
        if (isCurrentQuery()) {
          await onSuccess(result);
        }
      }
      return result;
    });

  pendingWrites.set(query, request);
  try {
    return await request;
  } finally {
    // A newer queued write may already own this entry; only remove our own queue tail.
    if (pendingWrites.get(query) === request) {
      pendingWrites.delete(query);
    }
  }
}

/**
 * PATCHes the profile and merges confirmed fields into its cache and the authenticator's retained snapshot.
 * Falls back to profile invalidation if the response has no ID; properties are not invalidated.
 */
export const updateCurrentUserData = (data: Parameters<typeof updateCurrentUser>[0]) =>
  mutateCurrentUser(
    () => updateCurrentUser(data),
    currentUserQueryKey(),
    async updatedUser => {
      if (!updatedUser.id) {
        await clearCurrentUserQueryCache();
        return;
      }
      const profile = queryClient.setQueryData<CurrentUserProfile>(currentUserQueryKey(), currentUser => ({
        ...currentUser,
        ...updatedUser
      }));
      appAuthenticator.updateCurrentUser(profile);
    }
  );

/**
 * Saves one property through the same guarded write and reconciliation path used for property batches.
 */
export const updateCurrentUserProperty = (property: Parameters<typeof updateUserProperty>[0]) =>
  updateCurrentUserProperties([property]);

/**
 * Saves properties sequentially, checking session validity before each write and after the batch.
 * Invalidates properties once if any write succeeded, including when a later write fails.
 * Only active observers refetch immediately; otherwise the next read reloads the invalidated data.
 */
export const updateCurrentUserProperties = (properties: Parameters<typeof updateUserProperty>[0][]) =>
  mutateCurrentUser(
    async isCurrent => {
      let changed = false;
      try {
        for (const property of properties) {
          if (!isCurrent()) {
            throw new Error('Current-user update canceled after authentication changed');
          }
          await updateUserProperty(property);
          changed = true;
        }
      } finally {
        if (changed && isCurrent()) {
          await clearUserPropertiesQueryCache();
        }
      }
      if (!isCurrent()) {
        throw new Error('Current-user update canceled after authentication changed');
      }
    },
    userPropertiesQueryKey(),
    // The batch's finally block already handles invalidation, including partial success.
    () => undefined
  );

/**
 * Wraps an imperative mutation in a MutationObserver for view-level pending/error state.
 * The caller captures isCurrent when saving: MutationObserver starts mutationFn asynchronously,
 * so capturing the session only inside mutationFn could attach an old save to a replacement session.
 */
const createCurrentUserMutation = <TData, TVariables>(mutation: (data: TVariables) => Promise<TData>) =>
  createMutation({
    mutationFn: ({ data, isCurrent }: { data: TVariables; isCurrent: () => boolean }) => {
      if (!isCurrent()) {
        throw new Error('Current-user update canceled after authentication changed');
      }
      return mutation(data);
    }
  });

/** Creates the guarded profile-update observer, including confirmed profile/cache synchronization. */
export const createUpdateCurrentUserMutation = () => createCurrentUserMutation(updateCurrentUserData);
